import React, { useState } from 'react';

import { Button, message, notification, Form, Modal, InputNumber } from 'antd';
import UserStore from 'stores/UserStore';
import SelectStates from 'components/shared/SelectStates/';
import { UploadFile, FormErrorParser } from '@partsbadger/library';
import ErrorRender from 'components/shared/ErrorRender';
import { FormItemProps } from '@ant-design/compatible/es/form';

import { ITaxExemptions } from 'types/types';

interface IProps {
    record?: ITaxExemptions;

    updateData: () => void;
}

const AddEdit: React.FC<IProps> = ({ record, updateData }) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>(null);
    const [visible, setVisible] = useState<boolean>(false);

    const [form] = Form.useForm();

    const handleSubmit = async () => {
        form.validateFields()
            .then(async (values: ITaxExemptions) => {
                const payload = {
                    states: values.states.map(value => value.key),
                    files: values.files.map(file => file.response.id),
                    sales_tax_deduction: values.sales_tax_deduction,
                };
                setLoading(true);
                setErrors(null);
                if (record) {
                    return await UserStore.editTaxExemption(payload, record.id)
                        .then(() => {
                            message.success(`Edited Successfully!!!`);
                            setVisible(false);
                            setLoading(false);
                            updateData();
                        })
                        .catch(error => {
                            setErrors(error);
                            setLoading(false);
                            notification.error({
                                message: 'Error',
                                description: <ErrorRender error={errors} />,
                            });
                        });
                }
                await UserStore.addTaxExemptions(payload)
                    .then(() => {
                        message.success(`Created Successfully!!!`);
                        form.resetFields();
                        setVisible(false);
                        setLoading(false);
                        setErrors(null);
                        updateData();
                    })
                    .catch(error => {
                        setErrors(error), setLoading(false);
                        notification.error({
                            message: 'Error',
                            description: <ErrorRender error={errors} />,
                        });
                    });
            })
            .catch(errorInfo => {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            });
    };

    const formItemLayout: FormItemProps = {
        labelCol: {
            // xs: { span: 24 },
            // sm: { span: 24 },
            md: { span: 24 },
        },
        wrapperCol: {
            // xs: { span: 24 },
            // sm: { span: 16 },
            md: { span: 24 },
        },
        labelAlign: 'left',
    };

    return (
        <>
            <>
                {record ? (
                    <Button
                        style={{ color: '#405767' }}
                        className="text-xl"
                        onClick={() => setVisible(!visible)}
                        loading={loading}
                        type="link"
                    >
                        Edit
                    </Button>
                ) : (
                    <Button
                        onClick={() => {
                            setVisible(true);
                        }}
                        loading={loading}
                        type="primary"
                    >
                        Add Exemptions
                    </Button>
                )}
            </>

            <Modal
                open={visible}
                title={record ? 'Edit Tax Exemption' : 'New Tax Exemption'}
                onCancel={() => setVisible(false)}
                footer={null}
            >
                <Form form={form}>
                    <Form.Item
                        label="State"
                        {...formItemLayout}
                        name="states"
                        rules={[{ required: true, message: 'Please input a name' }]}
                        initialValue={record?.states.map(row => {
                            return {
                                key: row,
                                label: row,
                            };
                        })}
                    >
                        <SelectStates countryShortCode="US" mode="multiple" />
                    </Form.Item>
                    <Form.Item
                        label="File"
                        {...formItemLayout}
                        name="files"
                        rules={[{ required: true, message: 'Please input a file' }]}
                        initialValue={record?.files.map(r => {
                            return {
                                uid: r.id,
                                name: r.filename,
                                status: 'done',
                                response: {
                                    id: r.id,
                                },
                                url: r.file,
                            };
                        })}
                    >
                        <UploadFile multiple={false} />
                    </Form.Item>

                    <Form.Item
                        label={'Partial Deduction'}
                        help={'Please enter if specified. Leave empty otherwise.'}
                        {...formItemLayout}
                        name="sales_tax_deduction"
                        rules={[{ required: false }]}
                        initialValue={record?.sales_tax_deduction}
                    >
                        <InputNumber
                            min={0}
                            max={100}
                            precision={4}
                            addonAfter="%"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <div className="flex justify-between mt-5">
                        <Button
                            key="back"
                            onClick={() => setVisible(false)}
                            size="large"
                            style={{ color: '#D92923', borderColor: '#D92923' }}
                        >
                            Close
                        </Button>
                        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit} size="large">
                            Submit
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default AddEdit;
